/**
 * COS 同源代理 URL 工具
 *
 * 后端接口：`GET /api/v1/util/cos_design/:cos_type/*file_path`
 * 由服务端完成私有桶鉴权与转发，前端无需再拼签名参数。
 *
 * @module @home/utils/htmlExport/cosProxy
 */

/**
 * 将 COS URL 转为同源代理 URL，避免 fetch / iframe 跨域限制
 *
 * @param cosUrl - 完整 COS URL（含 host）或相对路径（如 `databrain_agent/...`）
 * @param cosType - COS 配置类型，如 `'agentCos'` / `'dashboardAnomalyCos'`
 * @returns 同源代理路径；入参非法时返回空字符串
 *
 * @example
 * ```ts
 * toCosProxyUrl(
 *   'https://bucket.cos.ap-guangzhou.myqcloud.com/path/a.html',
 *   'agentCos',
 * );
 * // => '/api/v1/util/cos_design/agentCos/path/a.html'
 * ```
 */
export const toCosProxyUrl = (cosUrl: string, cosType: string): string => {
  if (!cosUrl || !cosType) {
    return '';
  }

  let path: string;
  if (cosUrl.startsWith('http://') || cosUrl.startsWith('https://')) {
    try {
      path = new URL(cosUrl).pathname;
    } catch {
      return '';
    }
  } else {
    path = cosUrl.startsWith('/') ? cosUrl : `/${cosUrl}`;
  }

  return `/api/v1/util/cos_design/${cosType}${path}`;
};

/**
 * 解析最终可请求的 URL：有 cosType 且开启代理时走代理，否则原样返回
 *
 * @param url - 原始 URL
 * @param cosType - COS 配置类型（可选）
 * @param useProxy - 是否强制走代理；默认有 cosType 时为 true
 */
export const resolveCosFetchUrl = (
  url: string,
  cosType?: string,
  useProxy?: boolean,
): string => {
  if (!url) {
    return '';
  }
  const shouldProxy = useProxy ?? !!cosType;
  if (shouldProxy && cosType) {
    return toCosProxyUrl(url, cosType) || url;
  }
  return url;
};
